import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { FaFileExcel } from 'react-icons/fa';
import { toast } from '../../toasts';
import paymentService from '../../services/payment.service';
import { getError, showCurrency } from '../../utils';
import { useLabels } from '../../hooks/useLabels';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { Section, StatStrip, DataTable, IdChip, RowActionsMenu, Button, ExportModal, buildXlsxExport, exportAmount } from '../../components/EntityList';
import { useClientSort } from '../../hooks/useClientSort';
import { dateValue, parseDueNumber } from '../../utils/tableSort';
import { isPaymentGroupsEnabled } from '../../utils/featureFlags';

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

/**
 * Rows are fetched once by the parent (DebtDetails) on page entry and passed
 * down here, rather than self-fetched on mount — this tab used to only fetch
 * when the user actually clicked into it, which showed a 0 count in the tab
 * label and a loading spinner every time the tab was revisited.
 */
function DebtPayments({ rows, loading, error, onRefetch }) {
	const { t } = useLabels();
	const { id } = useParams();
	const navigate = useNavigate();
	const [deletingPayment, setDeletingPayment] = useState(null);
	const [exportOpen, setExportOpen] = useState(false);

	const { sortedRows, sortColumn, direction, onSort } = useClientSort(rows, {
		defaultColumn: 'nro',
		// Several payments can pay the same due; those read in date order.
		tieBreakerColumn: 'date',
		accessors: {
			nro: (payment) => parseDueNumber(payment.debtDue?.customId),
			id: (payment) => payment.customId,
			due: (payment) => payment.debtDue?.customId,
			date: (payment) => dateValue(payment.date),
		},
	});

	const stats = useMemo(() => {
		const totalByCurrency = new Map();
		rows.forEach((payment) => {
			const key = payment.currency || '—';
			totalByCurrency.set(key, (totalByCurrency.get(key) || 0) + (payment.amount || 0));
		});
		return [
			{
				label: 'Total cobrado',
				value: [...totalByCurrency.entries()].map(([currency, total]) => formatMoney(currency, total)).join(' · ') || '—',
				mono: true,
			},
			{ label: 'Cantidad de cobros', value: rows.length },
		];
	}, [rows]);

	function handleShowPaymentModal() {
		navigate(`/AdminScreen/debts/debt/${id}/newPayment`);
	}

	function handleEditPayment(payment) {
		navigate(`/AdminScreen/payments/editPayment/${payment._id}`, {
			state: { returnTo: `/AdminScreen/debts/debt/${id}/cobros` },
		});
	}

	function verComprobante(payment) {
		if (payment.comprobante) {
			window.open(payment.comprobante.fileLink, '_blank', 'noopener,noreferrer');
		}
	}

	async function handleConfirmDelete() {
		try {
			await paymentService.delete(deletingPayment._id);
			toast.success(t('msg.paymentDeleted'));
			onRefetch?.();
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setDeletingPayment(null);
		}
	}

	const columns = [
		{
			key: 'nro',
			header: 'Nro',
			sortable: true,
			className: 'tw:font-mono tw:tabular-nums',
			render: (payment) => parseDueNumber(payment.debtDue?.customId) ?? <span className='tw:text-ink-3'>—</span>,
		},
		{ key: 'id', header: 'ID', sortable: true, render: (payment) => <IdChip>{payment.customId}</IdChip> },
		{ key: 'due', header: t('entity.due'), sortable: true, render: (payment) => payment.debtDue?.customId },
		{
			key: 'amount',
			header: t('field.amount'),
			className: 'tw:text-right',
			render: (payment) => <span className='tw:font-mono tw:tabular-nums'>{formatMoney(payment.currency, payment.amount)}</span>,
		},
		{ key: 'date', header: t('field.date'), sortable: true, render: (payment) => formatDate(payment.date) },
		{ key: 'folder', header: t('field.folder'), render: (payment) => payment.folder },
		{ key: 'status', header: t('field.status'), render: (payment) => payment.status },
		// One collection (bank transfer) covering several dues (SHOW_PAYMENT_GROUPS).
		isPaymentGroupsEnabled() && {
			key: 'group',
			header: 'Transferencia',
			render: (payment) => {
				const group = payment.group && typeof payment.group === 'object' ? payment.group : null;
				if (!group) return <span className='tw:text-ink-3'>—</span>;
				const count = (group.payments || []).length;
				return (
					<span className='tw:font-mono tw:tabular-nums tw:text-[12.5px]'>
						{group.reference || formatDate(group.date)} · {formatMoney(group.currency || payment.currency, group.amount)}
						{count > 1 ? ` · ${count} cuotas` : ''}
					</span>
				);
			},
		},
	].filter(Boolean);

	const exportFields = [
		{ key: 'nro', label: 'Nro' },
		{ key: 'id', label: 'ID', locked: true },
		{ key: 'due', label: t('entity.due') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'amount', label: t('field.amount') },
		{ key: 'date', label: t('field.date') },
		{ key: 'folder', label: t('field.folder') },
		{ key: 'status', label: t('field.status') },
	];

	const exportGetters = {
		nro: (payment) => parseDueNumber(payment.debtDue?.customId) ?? '',
		id: (payment) => payment.customId || '',
		due: (payment) => payment.debtDue?.customId || '',
		currency: (payment) => payment.currency || payment.debt?.currency || '',
		amount: (payment) => exportAmount(payment.amount),
		date: (payment) => formatDate(payment.date),
		folder: (payment) => payment.folder || '',
		status: (payment) => payment.status || '',
	};

	const handleExport = (selectedKeys) =>
		buildXlsxExport({
			rows: sortedRows,
			fields: exportFields,
			selectedKeys,
			getters: exportGetters,
			sheetName: 'Cobros',
			filePrefix: 'cobros',
		});

	const renderRowActions = (payment) => (
		<RowActionsMenu
			items={[
				{ label: t('action.edit'), onClick: () => handleEditPayment(payment) },
				payment.comprobante && { label: 'Ver comprobante', onClick: () => verComprobante(payment) },
				{ label: t('action.delete'), onClick: () => setDeletingPayment(payment), danger: true },
			]}
		/>
	);

	return (
		<div className='tw:flex tw:flex-col tw:gap-4'>
			<StatStrip stats={stats} />
			<div className='tw:flex tw:justify-end tw:gap-2'>
				<Button size='sm' icon={<FaFileExcel />} onClick={() => setExportOpen(true)} disabled={rows.length === 0}>
					{t('action.export')}
				</Button>
				<Button size='sm' variant='primary' onClick={handleShowPaymentModal}>
					{t('title.newPayment')}
				</Button>
			</div>
			<Section title={t('title.collections')}>
				<DataTable
					columns={columns}
					rows={sortedRows}
					loading={loading}
					error={error}
					sortColumn={sortColumn}
					direction={direction}
					onSort={onSort}
					onRowClick={handleEditPayment}
					rowActions={renderRowActions}
					emptyMessage='No se encontraron cobros para esta deuda.'
				/>
			</Section>

			<ExportModal
				show={exportOpen}
				onClose={() => setExportOpen(false)}
				count={rows.length}
				fields={exportFields}
				subtitle='Cobros de esta deuda'
				onExport={handleExport}
			/>

			<ConfirmationModal
				show={Boolean(deletingPayment)}
				handleClose={() => setDeletingPayment(null)}
				handleConfirm={handleConfirmDelete}
				title='Eliminar Cobro'
				description={t('msg.confirmDeletePayment')}
				confirmCaption={t('action.delete')}
				cancelCaption={t('action.cancel')}
			/>
		</div>
	);
}

export default DebtPayments;
